/**
 * PinnedMessagesPanel — Collapsible panel showing pinned messages
 * Used in ChannelInfoPage between action buttons and members list.
 */

import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { usePinnedMessages } from '../hooks';
import { ChatAvatar } from './ChatAvatar';

interface PinnedMsg {
  id: string;
  sender: { avatar?: string; name: string };
  createdAt: string;
  content?: string;
  type: string;
  pinnedBy?: { name: string };
}

interface PinnedMessagesPanelProps {
  conversationId: string;
}

export const PinnedMessagesPanel = memo(function PinnedMessagesPanel({
  conversationId,
}: PinnedMessagesPanelProps) {
  const { t } = useTranslation();
  const { data: pinnedMessages } = usePinnedMessages(conversationId);
  const [isExpanded, setIsExpanded] = useState(false);

  if (!pinnedMessages || pinnedMessages.length === 0) return null;

  return (
    <div className="mx-4 mb-3">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center justify-between rounded-xl border border-white/20 bg-white/40 px-4 py-3 transition-all hover:bg-white/50 active:scale-[0.99]"
      >
        <div className="flex items-center gap-2">
          {/* Pin icon */}
          <svg className="h-4 w-4 text-neutral-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 17v5" />
            <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 1 1 0 0 0 1-1V4a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1 1 1 0 0 1 1 1z" />
          </svg>
          <span className="text-sm font-medium text-neutral-700">
            {t('messaging.pinnedMessages')}
          </span>
          <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-xs font-bold text-blue-700">
            {pinnedMessages.length}
          </span>
        </div>
        <svg
          className={`h-4 w-4 text-neutral-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-1 space-y-1">
              {pinnedMessages.map((msg: PinnedMsg) => (
                <div
                  key={msg.id}
                  className="rounded-xl border border-white/20 bg-white/30 px-3 py-2"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <ChatAvatar src={msg.sender.avatar} name={msg.sender.name} size="h-5 w-5" />
                    <span className="text-xs font-medium text-neutral-700">{msg.sender.name}</span>
                    <span className="text-[10px] text-neutral-400">
                      {new Date(msg.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-neutral-600 line-clamp-2" dir="auto">
                    {msg.content || `[${msg.type}]`}
                  </p>
                  {msg.pinnedBy && (
                    <p className="mt-1 text-[10px] text-neutral-400">
                      {t('messaging.pinnedBy', { name: msg.pinnedBy.name })}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});
